import React, { useState, useCallback } from 'react';
import {
  BookOpen, Plus, Search, MoreHorizontal, CheckCircle, AlertTriangle,
  Edit2, Trash2, Eye, PlayCircle, Code, FileText, Settings, Clock,
  ToggleLeft, ToggleRight, Copy, ChevronRight, MessageSquare, Zap,
  Shield, Users, Globe, Lock, ArrowRight, RotateCcw, Send, User, Bot,
  Sparkles, Loader2, StepForward, ChevronDown, ChevronUp
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuTrigger, DropdownMenuSeparator
} from '../components/ui/dropdown-menu';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle, DialogTrigger
} from '../components/ui/dialog';
import {
  Tabs, TabsContent, TabsList, TabsTrigger
} from '../components/ui/tabs';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { Label } from '../components/ui/label';
import { cn } from '../lib/utils';

const mockGuidance = [
  {
    id: 'g-1',
    name: 'Password Reset Policy',
    description: 'Guidelines for handling password reset requests',
    type: 'always_on',
    scope: 'org_wide',
    enabled: true,
    content: `When handling password reset requests:
1. Always verify user identity via HRIS employee ID or manager confirmation
2. For admin accounts, require security team approval
3. Reset must use Okta admin console, never share temporary passwords
4. Log all reset actions in the audit trail
5. Notify user via registered email after reset`,
    triggersOnKeywords: ['password', 'reset', 'forgot', 'locked out', 'can\'t login'],
    lastModified: '2024-01-10',
    modifiedBy: 'Emily Zhang',
    usageCount7d: 234
  },
  {
    id: 'g-2',
    name: 'Elevated Access Approval',
    description: 'Rules for approving admin/elevated access requests',
    type: 'always_on',
    scope: 'org_wide',
    enabled: true,
    content: `Elevated access approval requirements:
1. Production access requires security team approval
2. Time-bound grants only (max 8 hours, renewable)
3. Must have ticket/incident reference
4. Notify user's manager of grant
5. Auto-revoke after timeout`,
    triggersOnKeywords: ['admin', 'elevated', 'production', 'root', 'sudo'],
    lastModified: '2024-01-12',
    modifiedBy: 'Alex Kim',
    usageCount7d: 45
  },
  {
    id: 'g-3',
    name: 'Hardware Request Validation',
    description: 'Checklist for hardware procurement requests',
    type: 'contextual',
    scope: 'team_only',
    team: 'Endpoint Management',
    enabled: true,
    content: `Before approving hardware requests:
1. Verify employee is in HRIS with active status
2. Check existing asset assignments
3. Confirm budget approval for items > $500
4. Standard equipment list applies unless exception approved
5. Shipping address must be verified company location or manager-approved remote`,
    triggersOnKeywords: ['laptop', 'macbook', 'equipment', 'hardware', 'device'],
    lastModified: '2024-01-08',
    modifiedBy: 'Taylor Brown',
    usageCount7d: 32
  },
  {
    id: 'g-4',
    name: 'Security Incident Response',
    description: 'Immediate actions for security alerts',
    type: 'always_on',
    scope: 'org_wide',
    enabled: true,
    content: `For any security incident:
1. CONTAIN FIRST: Disable account, terminate sessions
2. Preserve evidence: screenshot alerts, export logs
3. Notify security team immediately
4. Do not communicate with potentially compromised user
5. Document all actions with timestamps`,
    triggersOnKeywords: ['security', 'breach', 'suspicious', 'compromised', 'phishing', 'malware'],
    lastModified: '2024-01-14',
    modifiedBy: 'Alex Kim',
    usageCount7d: 18
  },
  {
    id: 'g-5',
    name: 'Contractor Onboarding',
    description: 'Limited scope onboarding for contractors',
    type: 'contextual',
    scope: 'team_only',
    team: 'IT Operations',
    enabled: false,
    content: `Contractor onboarding checklist:
1. Time-limited accounts only (match contract end date)
2. No access to PII systems without legal approval
3. External email domain allowed only with manager approval
4. No admin rights by default
5. Review access quarterly`,
    triggersOnKeywords: ['contractor', 'consultant', 'external', 'vendor'],
    lastModified: '2024-01-05',
    modifiedBy: 'David Park',
    usageCount7d: 8
  }
];

const typeConfig = {
  always_on: { label: 'Always On', description: 'Applied to all relevant requests automatically', icon: ToggleRight, color: 'text-green-600' },
  contextual: { label: 'Contextual', description: 'Applied when keywords match request content', icon: MessageSquare, color: 'text-blue-600' }
};

const scopeConfig = {
  org_wide: { label: 'Org-wide', icon: Globe, color: 'text-blue-600' },
  team_only: { label: 'Team Only', icon: Lock, color: 'text-amber-600' }
};

export const Guidance = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedScope, setSelectedScope] = useState('all');
  const [activeTab, setActiveTab] = useState('guidance');
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showTestDialog, setShowTestDialog] = useState(false);
  const [selectedGuidance, setSelectedGuidance] = useState(null);
  const [testInput, setTestInput] = useState('');
  const [testResult, setTestResult] = useState(null);
  const [guidance, setGuidance] = useState(mockGuidance);

  // Enhanced simulation harness state
  const [simulationMode, setSimulationMode] = useState('quick'); // 'quick' or 'conversation'
  const [conversationHistory, setConversationHistory] = useState([]);
  const [currentMessage, setCurrentMessage] = useState('');
  const [isSimulating, setIsSimulating] = useState(false);
  const [showGuidanceApplied, setShowGuidanceApplied] = useState(true);
  const [stepByStep, setStepByStep] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [expandedMessages, setExpandedMessages] = useState(new Set());

  const filteredGuidance = guidance.filter(g => {
    const matchesSearch = g.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      g.description.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesScope = selectedScope === 'all' || g.scope === selectedScope;
    return matchesSearch && matchesScope;
  });

  const stats = {
    total: guidance.length,
    alwaysOn: guidance.filter(g => g.type === 'always_on').length,
    enabled: guidance.filter(g => g.enabled).length,
    usageTotal: guidance.reduce((sum, g) => sum + g.usageCount7d, 0)
  };

  const toggleGuidance = (id) => {
    setGuidance(guidance.map(g =>
      g.id === id ? { ...g, enabled: !g.enabled } : g
    ));
  };

  const handleTestHarness = () => {
    const matchingGuidance = guidance.filter(g =>
      g.enabled && g.triggersOnKeywords.some(kw =>
        testInput.toLowerCase().includes(kw.toLowerCase())
      )
    );
    setTestResult(matchingGuidance);
  };

  // Simulated AI responses based on guidance
  const getSimulatedResponse = useCallback((input, matchedGuidance) => {
    const simulatedResponses = {
      'password': "I'll help you with your password reset. Based on our security policy, I need to verify your identity first. Could you please confirm your employee ID from HRIS?",
      'admin': "For elevated access requests, I'll need to route this through our security team for approval. Access will be time-bound (maximum 8 hours). Do you have a ticket or incident reference for this request?",
      'laptop': "I can help with your hardware request. Let me verify a few things: First, I'm checking your HRIS status and existing asset assignments. For items over $500, we'll need budget approval. What equipment are you requesting?",
      'security': "This appears to be a security-related matter. Following our incident response protocol, I'm prioritizing containment. Could you provide more details about what you observed? I'll coordinate with the security team.",
      'vpn': "I'll help troubleshoot your VPN connection. Let's start with some basic checks. Are you connected to the internet? Have you tried restarting the VPN client?",
      'default': "I understand your request. Let me help you with that. Could you provide more details so I can assist you better?"
    };

    const lowerInput = input.toLowerCase();
    let response = simulatedResponses.default;

    for (const [keyword, resp] of Object.entries(simulatedResponses)) {
      if (keyword !== 'default' && lowerInput.includes(keyword)) {
        response = resp;
        break;
      }
    }

    return {
      response,
      guidanceApplied: matchedGuidance,
      timestamp: new Date().toISOString()
    };
  }, []);

  const handleSendSimulation = useCallback(async () => {
    if (!currentMessage.trim()) return;

    setIsSimulating(true);

    // Add user message
    const userMsg = {
      id: Date.now(),
      type: 'user',
      content: currentMessage,
      timestamp: new Date().toISOString()
    };

    // Find matching guidance
    const matchedGuidance = guidance.filter(g =>
      g.enabled && g.triggersOnKeywords.some(kw =>
        currentMessage.toLowerCase().includes(kw.toLowerCase())
      )
    );

    // Simulate processing delay
    await new Promise(resolve => setTimeout(resolve, 1200));

    // Generate AI response
    const aiResponse = getSimulatedResponse(currentMessage, matchedGuidance);
    const aiMsg = {
      id: Date.now() + 1,
      type: 'assistant',
      content: aiResponse.response,
      guidanceApplied: aiResponse.guidanceApplied,
      timestamp: aiResponse.timestamp
    };

    setConversationHistory(prev => [...prev, userMsg, aiMsg]);
    setCurrentMessage('');
    setIsSimulating(false);
  }, [currentMessage, guidance, getSimulatedResponse]);

  const handleResetSimulation = useCallback(() => {
    setConversationHistory([]);
    setCurrentMessage('');
    setTestInput('');
    setTestResult(null);
    setCurrentStep(0);
  }, []);

  const toggleMessageExpand = useCallback((msgId) => {
    setExpandedMessages(prev => {
      const next = new Set(prev);
      if (next.has(msgId)) {
        next.delete(msgId);
      } else {
        next.add(msgId);
      }
      return next;
    });
  }, []);

  return (
    <div className="min-h-full bg-background">
      <div className="border-b bg-card">
        <div className="max-w-6xl mx-auto px-6 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-semibold text-foreground">Guidance</h1>
              <p className="text-sm text-muted-foreground mt-1">
                Define agent behavior rules and always-on guidance
              </p>
            </div>
            <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
              <DialogTrigger asChild>
                <Button>
                  <Plus className="w-4 h-4 mr-2" />
                  Create Guidance
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl">
                <DialogHeader>
                  <DialogTitle>Create Guidance Rule</DialogTitle>
                  <DialogDescription>
                    Define behavior rules that guide AI agent responses
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4 py-4">
                  <div className="space-y-2">
                    <Label>Name</Label>
                    <Input placeholder="e.g., VPN Troubleshooting Guide" />
                  </div>
                  <div className="space-y-2">
                    <Label>Description</Label>
                    <Input placeholder="Brief description of when this applies" />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Type</Label>
                      <Select defaultValue="always_on">
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="always_on">Always On</SelectItem>
                          <SelectItem value="contextual">Contextual (keyword-triggered)</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Scope</Label>
                      <Select defaultValue="org_wide">
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="org_wide">Organization-wide</SelectItem>
                          <SelectItem value="team_only">Team Only</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>Trigger Keywords (comma-separated)</Label>
                    <Input placeholder="e.g., vpn, connection, network, tunnel" />
                  </div>
                  <div className="space-y-2">
                    <Label>Guidance Content</Label>
                    <Textarea
                      placeholder="Enter the guidance rules, checklist, or instructions..."
                      rows={6}
                    />
                    <p className="text-xs text-muted-foreground">
                      This content will be provided to the AI agent when handling relevant requests.
                    </p>
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setShowCreateDialog(false)}>Cancel</Button>
                  <Button onClick={() => setShowCreateDialog(false)}>Create Guidance</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-8">
        <div className="grid grid-cols-4 gap-4 mb-8">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                  <BookOpen className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-foreground">{stats.total}</p>
                  <p className="text-xs text-muted-foreground">Total Rules</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
                  <ToggleRight className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-foreground">{stats.alwaysOn}</p>
                  <p className="text-xs text-muted-foreground">Always On</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                  <CheckCircle className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-foreground">{stats.enabled}</p>
                  <p className="text-xs text-muted-foreground">Enabled</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
                  <Zap className="w-5 h-5 text-amber-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-foreground">{stats.usageTotal}</p>
                  <p className="text-xs text-muted-foreground">Applied (7d)</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <div className="flex items-center justify-between">
            <TabsList>
              <TabsTrigger value="guidance">Guidance Rules</TabsTrigger>
              <TabsTrigger value="test">Test Harness</TabsTrigger>
            </TabsList>

            {activeTab === 'guidance' && (
              <div className="flex items-center gap-4">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    placeholder="Search guidance..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-9 w-64"
                  />
                </div>
                <Select value={selectedScope} onValueChange={setSelectedScope}>
                  <SelectTrigger className="w-36">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Scopes</SelectItem>
                    <SelectItem value="org_wide">Org-wide</SelectItem>
                    <SelectItem value="team_only">Team Only</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>

          <TabsContent value="guidance" className="space-y-4">
            {filteredGuidance.map((item) => {
              const type = typeConfig[item.type];
              const scope = scopeConfig[item.scope];
              const TypeIcon = type.icon;
              const ScopeIcon = scope.icon;

              return (
                <Card key={item.id} className={cn(!item.enabled && "opacity-60")}>
                  <CardContent className="p-5">
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                        <BookOpen className="w-5 h-5 text-primary" />
                      </div>

                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="text-base font-semibold text-foreground">{item.name}</h3>
                          <Badge variant="outline" className="text-xs">
                            <TypeIcon className={cn("w-3 h-3 mr-1", type.color)} />
                            {type.label}
                          </Badge>
                          <Badge variant="outline" className="text-xs">
                            <ScopeIcon className={cn("w-3 h-3 mr-1", scope.color)} />
                            {scope.label}
                          </Badge>
                          {!item.enabled && (
                            <Badge variant="secondary" className="text-xs">Disabled</Badge>
                          )}
                        </div>
                        <p className="text-sm text-muted-foreground">{item.description}</p>

                        <div className="mt-3 flex items-center gap-4 text-xs text-muted-foreground">
                          <span className="flex items-center gap-1">
                            <Zap className="w-3 h-3" />
                            {item.usageCount7d} uses (7d)
                          </span>
                          <span className="flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            Modified {item.lastModified}
                          </span>
                          {item.team && (
                            <span className="flex items-center gap-1">
                              <Users className="w-3 h-3" />
                              {item.team}
                            </span>
                          )}
                        </div>

                        <div className="mt-3 flex flex-wrap gap-1">
                          {item.triggersOnKeywords.slice(0, 5).map((kw, idx) => (
                            <Badge key={idx} variant="secondary" className="text-xs">{kw}</Badge>
                          ))}
                          {item.triggersOnKeywords.length > 5 && (
                            <Badge variant="secondary" className="text-xs">
                              +{item.triggersOnKeywords.length - 5} more
                            </Badge>
                          )}
                        </div>
                      </div>

                      <div className="flex items-center gap-3 shrink-0">
                        <Switch
                          checked={item.enabled}
                          onCheckedChange={() => toggleGuidance(item.id)}
                        />
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setSelectedGuidance(item);
                            setShowTestDialog(true);
                          }}
                        >
                          <Eye className="w-4 h-4 mr-2" />
                          View
                        </Button>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon">
                              <MoreHorizontal className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem>
                              <Edit2 className="w-4 h-4 mr-2" />
                              Edit
                            </DropdownMenuItem>
                            <DropdownMenuItem>
                              <Copy className="w-4 h-4 mr-2" />
                              Duplicate
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem className="text-destructive">
                              <Trash2 className="w-4 h-4 mr-2" />
                              Delete
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </TabsContent>

          <TabsContent value="test">
            <div className="grid grid-cols-3 gap-6">
              {/* Simulation Controls */}
              <div className="col-span-1 space-y-4">
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-sm flex items-center gap-2">
                      <Settings className="w-4 h-4" aria-hidden="true" />
                      Simulation Settings
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="space-y-2">
                      <Label className="text-xs">Mode</Label>
                      <Select value={simulationMode} onValueChange={setSimulationMode}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="quick">Quick Test</SelectItem>
                          <SelectItem value="conversation">Conversation Simulation</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="flex items-center justify-between">
                      <Label className="text-xs">Show applied guidance</Label>
                      <Switch
                        checked={showGuidanceApplied}
                        onCheckedChange={setShowGuidanceApplied}
                      />
                    </div>

                    <Button
                      variant="outline"
                      size="sm"
                      className="w-full"
                      onClick={handleResetSimulation}
                    >
                      <RotateCcw className="w-3 h-3 mr-2" aria-hidden="true" />
                      Reset Simulation
                    </Button>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-sm flex items-center gap-2">
                      <BookOpen className="w-4 h-4" aria-hidden="true" />
                      Active Guidance ({guidance.filter(g => g.enabled).length})
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2 max-h-64 overflow-y-auto">
                    {guidance.filter(g => g.enabled).map(g => (
                      <div
                        key={g.id}
                        className="p-2 rounded border bg-muted/30 text-xs"
                      >
                        <div className="font-medium text-foreground">{g.name}</div>
                        <div className="text-muted-foreground mt-0.5 truncate">
                          Keywords: {g.triggersOnKeywords.slice(0, 3).join(', ')}
                          {g.triggersOnKeywords.length > 3 && '...'}
                        </div>
                      </div>
                    ))}
                  </CardContent>
                </Card>
              </div>

              {/* Main Simulation Area */}
              <div className="col-span-2">
                {simulationMode === 'quick' ? (
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-base flex items-center gap-2">
                        <PlayCircle className="w-5 h-5 text-primary" aria-hidden="true" />
                        Quick Guidance Test
                      </CardTitle>
                      <CardDescription>
                        Test which guidance rules will be applied for a given request
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div className="space-y-2">
                        <Label>Sample Request Content</Label>
                        <Textarea
                          placeholder="Enter a sample request, e.g., 'I need to reset my password, I forgot it and can't login'"
                          value={testInput}
                          onChange={(e) => setTestInput(e.target.value)}
                          rows={3}
                        />
                      </div>
                      <Button onClick={handleTestHarness} disabled={!testInput.trim()}>
                        <PlayCircle className="w-4 h-4 mr-2" aria-hidden="true" />
                        Test Guidance Matching
                      </Button>

                      {testResult && (
                        <div className="mt-6 space-y-4">
                          <div className="flex items-center justify-between">
                            <h4 className="text-sm font-medium text-foreground">
                              {testResult.length} guidance rule(s) would be applied
                            </h4>
                            {testResult.length > 0 && (
                              <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                                <CheckCircle className="w-3 h-3 mr-1" aria-hidden="true" />
                                Rules Matched
                              </Badge>
                            )}
                          </div>
                          {testResult.length === 0 ? (
                            <div className="p-4 bg-muted/50 rounded-lg text-center">
                              <AlertTriangle className="w-8 h-8 text-amber-500 mx-auto mb-2" aria-hidden="true" />
                              <p className="text-sm text-muted-foreground">
                                No guidance rules match this request. Consider creating relevant guidance.
                              </p>
                            </div>
                          ) : (
                            testResult.map((g) => (
                              <Card key={g.id} className="bg-green-50 border-green-200">
                                <CardContent className="p-4">
                                  <div className="flex items-start gap-3">
                                    <CheckCircle className="w-5 h-5 text-green-600 mt-0.5" aria-hidden="true" />
                                    <div className="flex-1">
                                      <h5 className="text-sm font-medium text-green-900">{g.name}</h5>
                                      <p className="text-xs text-green-700 mt-1">
                                        Matched keywords: {g.triggersOnKeywords.filter(kw =>
                                          testInput.toLowerCase().includes(kw.toLowerCase())
                                        ).join(', ')}
                                      </p>
                                      <pre className="mt-2 p-3 bg-white rounded text-xs text-slate-700 whitespace-pre-wrap">
                                        {g.content}
                                      </pre>
                                    </div>
                                  </div>
                                </CardContent>
                              </Card>
                            ))
                          )}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                ) : (
                  <Card className="h-full flex flex-col">
                    <CardHeader className="pb-3">
                      <CardTitle className="text-base flex items-center gap-2">
                        <MessageSquare className="w-5 h-5 text-primary" aria-hidden="true" />
                        Conversation Simulation
                      </CardTitle>
                      <CardDescription>
                        Simulate a full conversation to see how guidance affects agent responses
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="flex-1 flex flex-col">
                      {/* Conversation History */}
                      <div className="flex-1 space-y-4 mb-4 min-h-[300px] max-h-[400px] overflow-y-auto p-2 border rounded-lg bg-muted/20">
                        {conversationHistory.length === 0 ? (
                          <div className="h-full flex items-center justify-center text-muted-foreground">
                            <div className="text-center">
                              <MessageSquare className="w-8 h-8 mx-auto mb-2 opacity-50" aria-hidden="true" />
                              <p className="text-sm">Start a conversation to test guidance</p>
                              <p className="text-xs mt-1">Try: "I forgot my password" or "I need admin access"</p>
                            </div>
                          </div>
                        ) : (
                          conversationHistory.map((msg) => (
                            <div key={msg.id} className={cn(
                              "flex gap-3",
                              msg.type === 'user' ? "justify-end" : "justify-start"
                            )}>
                              {msg.type === 'assistant' && (
                                <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                                  <Bot className="w-4 h-4 text-primary" aria-hidden="true" />
                                </div>
                              )}
                              <div className={cn(
                                "max-w-[80%] space-y-2",
                                msg.type === 'user' ? "items-end" : "items-start"
                              )}>
                                <div className={cn(
                                  "rounded-lg px-4 py-2 text-sm",
                                  msg.type === 'user'
                                    ? "bg-primary text-primary-foreground"
                                    : "bg-card border"
                                )}>
                                  {msg.content}
                                </div>
                                {msg.type === 'assistant' && showGuidanceApplied && msg.guidanceApplied?.length > 0 && (
                                  <div className="space-y-1">
                                    <button
                                      className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
                                      onClick={() => toggleMessageExpand(msg.id)}
                                    >
                                      <Sparkles className="w-3 h-3 text-amber-500" aria-hidden="true" />
                                      {msg.guidanceApplied.length} guidance rule(s) applied
                                      {expandedMessages.has(msg.id) ? (
                                        <ChevronUp className="w-3 h-3" aria-hidden="true" />
                                      ) : (
                                        <ChevronDown className="w-3 h-3" aria-hidden="true" />
                                      )}
                                    </button>
                                    {expandedMessages.has(msg.id) && (
                                      <div className="space-y-1 pl-4 border-l-2 border-amber-200">
                                        {msg.guidanceApplied.map(g => (
                                          <div key={g.id} className="text-xs p-2 bg-amber-50 rounded border border-amber-200">
                                            <span className="font-medium text-amber-800">{g.name}</span>
                                          </div>
                                        ))}
                                      </div>
                                    )}
                                  </div>
                                )}
                              </div>
                              {msg.type === 'user' && (
                                <div className="w-8 h-8 rounded-full bg-slate-200 flex items-center justify-center shrink-0">
                                  <User className="w-4 h-4 text-slate-600" aria-hidden="true" />
                                </div>
                              )}
                            </div>
                          ))
                        )}
                        {isSimulating && (
                          <div className="flex gap-3 justify-start">
                            <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                              <Bot className="w-4 h-4 text-primary" aria-hidden="true" />
                            </div>
                            <div className="rounded-lg px-4 py-2 bg-card border">
                              <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" aria-label="Processing" />
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Input Area */}
                      <div className="flex gap-2">
                        <Input
                          placeholder="Type a message to test guidance..."
                          value={currentMessage}
                          onChange={(e) => setCurrentMessage(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' && !e.shiftKey) {
                              e.preventDefault();
                              handleSendSimulation();
                            }
                          }}
                          disabled={isSimulating}
                        />
                        <Button
                          onClick={handleSendSimulation}
                          disabled={!currentMessage.trim() || isSimulating}
                        >
                          {isSimulating ? (
                            <Loader2 className="w-4 h-4 animate-spin" aria-label="Sending" />
                          ) : (
                            <Send className="w-4 h-4" aria-hidden="true" />
                          )}
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                )}
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </div>

      <Dialog open={showTestDialog} onOpenChange={setShowTestDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{selectedGuidance?.name}</DialogTitle>
            <DialogDescription>{selectedGuidance?.description}</DialogDescription>
          </DialogHeader>
          {selectedGuidance && (
            <div className="py-4 space-y-4">
              <div className="flex items-center gap-4">
                <Badge variant="outline">
                  {typeConfig[selectedGuidance.type].label}
                </Badge>
                <Badge variant="outline">
                  {scopeConfig[selectedGuidance.scope].label}
                </Badge>
                {selectedGuidance.team && (
                  <Badge variant="outline">Team: {selectedGuidance.team}</Badge>
                )}
              </div>
              <div>
                <Label className="text-xs text-muted-foreground">Trigger Keywords</Label>
                <div className="flex flex-wrap gap-1 mt-1">
                  {selectedGuidance.triggersOnKeywords.map((kw, idx) => (
                    <Badge key={idx} variant="secondary" className="text-xs">{kw}</Badge>
                  ))}
                </div>
              </div>
              <div>
                <Label className="text-xs text-muted-foreground">Guidance Content</Label>
                <pre className="mt-2 p-4 bg-muted rounded-lg text-sm whitespace-pre-wrap">
                  {selectedGuidance.content}
                </pre>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowTestDialog(false)}>Close</Button>
            <Button>
              <Edit2 className="w-4 h-4 mr-2" />
              Edit Guidance
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Guidance;
